import { flokService } from '@/modules/flokee/services/flok.service';
import { useEcommerceStore } from '@/modules/flokee/stores/ecommerce.store';
import { useTiendanubeInstallationStore } from './installation.auth';

const TIENDANUBE_APP_ID = import.meta.env.VITE_APP_TIENDANUBE_APP_ID;

export function getTiendanubeMarketplaceAuthorizationUrl(): string {
  return `https://www.tiendanube.com/apps/${TIENDANUBE_APP_ID}/authorize`;
}

export async function getAuthenticatedTiendanubeAuthorizationUrl(
  ecommerceId = useEcommerceStore().current_ecommerce_id,
): Promise<string> {
  // Invariant (2) of the installation flow, in `auth.store`: captured BEFORE
  // the state request, because a selection change while it is in flight would
  // otherwise be recorded as the store this authorization was started for, and
  // the returning install would be treated as a reconnect for it.
  const started_for_ecommerce_id = ecommerceId;
  const { data: response } =
    await flokService.createTiendanubeAuthorizationState(started_for_ecommerce_id);
  const state = response?.data?.state;
  // The state's own expiry, which is what makes this association claimable or
  // dead — the installation ticket's hour starts later, at the callback.
  const expires_at = response?.data?.expires_at;

  if (typeof state !== 'string' || state.length === 0) {
    throw new Error('Flokee did not create a Tiendanube authorization state. Please try again.');
  }

  // Every in-app authorization goes through here — the integration-failed
  // notice, store settings, widgets and the provider step — and this is the
  // only moment the store it is for is known: what returns from Tiendanube
  // names a Tiendanube store, not one of ours. FLOK-1717.
  useTiendanubeInstallationStore().recordTiendanubeAuthorization(
    state,
    started_for_ecommerce_id,
    expires_at,
  );

  const authorizationUrl = new URL(getTiendanubeMarketplaceAuthorizationUrl());
  authorizationUrl.searchParams.set('state', state);

  return authorizationUrl.toString();
}

export async function openAuthenticatedTiendanubeAuthorization(
  target: '_self' | '_blank' = '_self',
): Promise<void> {
  const authorizationWindow = target === '_blank' ? window.open('about:blank', target) : null;

  try {
    const authorizationUrl = await getAuthenticatedTiendanubeAuthorizationUrl();

    if (authorizationWindow) {
      authorizationWindow.location.replace(authorizationUrl);
    } else {
      window.location.assign(authorizationUrl);
    }
  } catch (error) {
    authorizationWindow?.close();
    throw error;
  }
}
